import { useEffect, useState } from 'react';
import {
  transferProtocol,
  type TransferRequest,
  type TransferStatus,
  type TransferType,
  type TransferReason,
  type MDAInfo,
} from '@/services/interMDA';
import styles from './InterMDATransferPage.module.css';

// Mock MDA data
const mockMDAs: MDAInfo[] = [
  { code: 'OHCS', name: 'Office of the Head of Civil Service', registryContact: { name: 'Ama Mensah', email: 'registry@ohcs.gov.gh', phone: '+233 30 266 4271' }, address: 'State House, Accra' },
  { code: 'MOF', name: 'Ministry of Finance', registryContact: { name: 'Kofi Asante', email: 'registry@mof.gov.gh', phone: '+233 30 266 8410' }, address: 'Finance Drive, Accra' },
  { code: 'MOH', name: 'Ministry of Health', registryContact: { name: 'Akua Owusu', email: 'registry@moh.gov.gh', phone: '+233 30 266 6151' }, address: 'Ring Road East, Accra' },
  { code: 'MOE', name: 'Ministry of Education', registryContact: { name: 'Yaw Boateng', email: 'registry@moe.gov.gh', phone: '+233 30 266 7891' }, address: 'Accra' },
  { code: 'MOTI', name: 'Ministry of Trade & Industry', registryContact: { name: 'Efua Appiah', email: 'registry@moti.gov.gh', phone: '+233 30 266 8421' }, address: 'Airport City, Accra' },
];

// Mock transfer requests
function generateMockTransfers(): TransferRequest[] {
  return [
    {
      id: 'trf_001',
      requestNumber: 'TRF-2024-0023',
      type: 'permanent_transfer',
      status: 'hub_processing',
      priority: 'normal',
      pfileId: 'pf_001',
      fileNumber: 'OHCS-2015-0342',
      staffId: 'staff_001',
      staffName: 'Kwame Asante Mensah',
      currentGrade: 'Grade 16',
      sourceMDA: mockMDAs[0],
      destinationMDA: mockMDAs[1],
      ohcsHub: { name: 'OHCS Central Records Hub', processingOfficer: 'Admin User', processedAt: new Date() },
      reason: 'promotion_transfer',
      effectiveDate: new Date(2024, 2, 1),
      requestedBy: 'user_001',
      requestedAt: new Date(Date.now() - 259200000), // 3 days ago
      dispatchedAt: new Date(Date.now() - 172800000),
      attachments: [],
      verificationStatus: 'in_progress',
      verifications: [
        { id: 'v1', type: 'identity_verification', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
        { id: 'v2', type: 'record_completeness', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
        { id: 'v3', type: 'authorization_check', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
        { id: 'v4', type: 'file_number_validation', status: 'pending' },
        { id: 'v5', type: 'service_history_check', status: 'pending' },
        { id: 'v6', type: 'outstanding_issues', status: 'pending' },
      ],
      timeline: [
        { id: 'e1', type: 'request_created', timestamp: new Date(Date.now() - 259200000), actor: 'user_001', actorRole: 'Registry Officer', description: 'Transfer request created' },
        { id: 'e2', type: 'request_submitted', timestamp: new Date(Date.now() - 259200000), actor: 'user_001', actorRole: 'Registry Officer', description: 'Request submitted for approval' },
        { id: 'e3', type: 'approval_granted', timestamp: new Date(Date.now() - 216000000), actor: 'HR Director', actorRole: 'Source MDA HR Director', description: 'Transfer approved by OHCS' },
        { id: 'e4', type: 'dispatched', timestamp: new Date(Date.now() - 172800000), actor: 'user_001', actorRole: 'Registry Officer', description: 'Dispatched to OHCS Hub' },
        { id: 'e5', type: 'hub_received', timestamp: new Date(Date.now() - 86400000), actor: 'Admin', actorRole: 'OHCS Processing Officer', description: 'Received at OHCS Hub' },
      ],
      canRollback: false,
    },
    {
      id: 'trf_002',
      requestNumber: 'TRF-2024-0019',
      type: 'secondment',
      status: 'completed',
      priority: 'urgent',
      pfileId: 'pf_002',
      fileNumber: 'MOF-2012-0156',
      staffId: 'staff_002',
      staffName: 'Abena Osei-Bonsu',
      currentGrade: 'Grade 17',
      sourceMDA: mockMDAs[1],
      destinationMDA: mockMDAs[0],
      ohcsHub: { name: 'OHCS Central Records Hub', processingOfficer: 'Admin User', verificationOfficer: 'Senior Admin', processedAt: new Date(Date.now() - 604800000), verifiedAt: new Date(Date.now() - 518400000) },
      reason: 'secondment',
      effectiveDate: new Date(2024, 1, 15),
      requestedBy: 'user_002',
      requestedAt: new Date(Date.now() - 1209600000), // 14 days ago
      dispatchedAt: new Date(Date.now() - 1123200000),
      receivedAt: new Date(Date.now() - 432000000),
      completedAt: new Date(Date.now() - 345600000),
      attachments: [],
      verificationStatus: 'verified',
      verifications: [
        { id: 'v1', type: 'identity_verification', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
        { id: 'v2', type: 'record_completeness', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
        { id: 'v3', type: 'authorization_check', status: 'passed', verifiedBy: 'Admin', verifiedAt: new Date() },
      ],
      timeline: [],
      canRollback: true,
      rollbackDeadline: new Date(Date.now() + 1728000000),
    },
    {
      id: 'trf_003',
      requestNumber: 'TRF-2024-0028',
      type: 'permanent_transfer',
      status: 'pending_approval',
      priority: 'normal',
      pfileId: 'pf_003',
      fileNumber: 'MOH-2018-0089',
      staffId: 'staff_003',
      staffName: 'Kofi Amoako',
      currentGrade: 'Grade 14',
      sourceMDA: mockMDAs[2],
      destinationMDA: mockMDAs[3],
      ohcsHub: { name: 'OHCS Central Records Hub' },
      reason: 'posting_transfer',
      effectiveDate: new Date(2024, 3, 1),
      requestedBy: 'user_003',
      requestedAt: new Date(Date.now() - 86400000), // 1 day ago
      attachments: [],
      verificationStatus: 'pending',
      verifications: [],
      timeline: [
        { id: 'e1', type: 'request_created', timestamp: new Date(Date.now() - 86400000), actor: 'user_003', actorRole: 'Registry Officer', description: 'Transfer request created' },
        { id: 'e2', type: 'request_submitted', timestamp: new Date(Date.now() - 86400000), actor: 'user_003', actorRole: 'Registry Officer', description: 'Request submitted for approval' },
      ],
      canRollback: false,
    },
  ];
}

export function InterMDATransferPage() {
  const [transfers, setTransfers] = useState<TransferRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'new' | 'tracking' | 'verification'>('dashboard');
  const [selectedTransfer, setSelectedTransfer] = useState<TransferRequest | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // New transfer form state
  const [newTransfer, setNewTransfer] = useState({
    type: 'permanent_transfer' as TransferType,
    priority: 'normal' as 'urgent' | 'normal' | 'low',
    fileNumber: '',
    staffName: '',
    staffId: '',
    currentGrade: '',
    sourceMDA: '',
    destinationMDA: '',
    reason: 'posting_transfer' as TransferReason,
    effectiveDate: '',
    notes: '',
  });

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const mockTransfers = generateMockTransfers();
        setTransfers(mockTransfers);
      } catch (err) {
        console.error('Failed to fetch transfers:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  function handleCreateTransfer() {
    if (!newTransfer.fileNumber || !newTransfer.staffName || !newTransfer.sourceMDA || !newTransfer.destinationMDA) return;

    const sourceMDA = mockMDAs.find(m => m.code === newTransfer.sourceMDA)!;
    const destMDA = mockMDAs.find(m => m.code === newTransfer.destinationMDA)!;

    const transfer = transferProtocol.createTransferRequest({
      type: newTransfer.type,
      priority: newTransfer.priority,
      pfileId: `pf_${Date.now()}`,
      fileNumber: newTransfer.fileNumber,
      staffId: newTransfer.staffId || `staff_${Date.now()}`,
      staffName: newTransfer.staffName,
      currentGrade: newTransfer.currentGrade,
      sourceMDA,
      destinationMDA: destMDA,
      reason: newTransfer.reason,
      effectiveDate: new Date(newTransfer.effectiveDate),
      requestedBy: 'current_user',
      notes: newTransfer.notes,
    });

    setTransfers([transfer, ...transfers]);
    setNewTransfer({
      type: 'permanent_transfer',
      priority: 'normal',
      fileNumber: '',
      staffName: '',
      staffId: '',
      currentGrade: '',
      sourceMDA: '',
      destinationMDA: '',
      reason: 'posting_transfer',
      effectiveDate: '',
      notes: '',
    });
    setActiveTab('tracking');
  }

  function getStatusColor(status: TransferStatus): string {
    switch (status) {
      case 'completed':
        return 'var(--success)';
      case 'rejected':
      case 'cancelled':
      case 'rolled_back':
        return 'var(--error)';
      case 'hub_verified':
      case 'received':
        return 'var(--ghana-green)';
      case 'hub_processing':
      case 'in_transit':
      case 'dispatched':
        return 'var(--info)';
      case 'pending_approval':
      case 'approved':
        return 'var(--warning)';
      default:
        return 'var(--text-muted)';
    }
  }

  function getPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent': return 'var(--error)';
      case 'normal': return 'var(--info)';
      case 'low': return 'var(--text-muted)';
      default: return 'var(--text-muted)';
    }
  }

  function formatDate(date: Date | string): string {
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  function getVerificationProgress(transfer: TransferRequest): number {
    if (!transfer.verifications.length) return 0;
    const completed = transfer.verifications.filter(v => v.status !== 'pending').length;
    return Math.round((completed / transfer.verifications.length) * 100);
  }

  // Stats calculations
  const pendingCount = transfers.filter(t => !['completed', 'rejected', 'cancelled', 'rolled_back'].includes(t.status)).length;
  const completedCount = transfers.filter(t => t.status === 'completed').length;
  const inTransitCount = transfers.filter(t => ['dispatched', 'hub_processing', 'hub_verified', 'in_transit'].includes(t.status)).length;

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.loadingSpinner} />
          <span>Loading Transfer Management...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerBadge}>Inter-MDA</div>
          <h1 className={styles.title}>Records Transfer</h1>
          <p className={styles.subtitle}>
            Manage transfers between MDAs through the OHCS Central Hub
          </p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.primaryButton} onClick={() => setActiveTab('new')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New Transfer
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className={styles.tabs}>
        {[
          { id: 'dashboard', label: 'Dashboard', icon: '📊' },
          { id: 'new', label: 'New Transfer', icon: '📤' },
          { id: 'tracking', label: 'Track Transfers', icon: '📋', badge: pendingCount },
          { id: 'verification', label: 'Verification', icon: '✓' },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
            onClick={() => setActiveTab(tab.id as typeof activeTab)}
          >
            <span className={styles.tabIcon}>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.badge && tab.badge > 0 && <span className={styles.tabBadge}>{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className={styles.content}>
          {/* Stats */}
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>📊</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{transfers.length}</span>
                <span className={styles.statLabel}>Total Transfers</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>⏳</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{pendingCount}</span>
                <span className={styles.statLabel}>In Progress</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>🚚</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{inTransitCount}</span>
                <span className={styles.statLabel}>In Transit</span>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>✓</div>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{completedCount}</span>
                <span className={styles.statLabel}>Completed</span>
              </div>
            </div>
          </div>

          {/* Recent Transfers */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Recent Transfers</h3>
            <div className={styles.transferList}>
              {transfers.slice(0, 5).map((transfer) => (
                <div key={transfer.id} className={styles.transferCard} onClick={() => { setSelectedTransfer(transfer); setShowDetailModal(true); }}>
                  <div className={styles.transferHeader}>
                    <div className={styles.transferInfo}>
                      <span className={styles.transferNumber}>{transfer.requestNumber}</span>
                      <span
                        className={styles.transferStatus}
                        style={{ backgroundColor: `${getStatusColor(transfer.status)}20`, color: getStatusColor(transfer.status) }}
                      >
                        {transferProtocol.getStatusLabel(transfer.status)}
                      </span>
                      <span
                        className={styles.priorityBadge}
                        style={{ backgroundColor: `${getPriorityColor(transfer.priority)}20`, color: getPriorityColor(transfer.priority) }}
                      >
                        {transfer.priority}
                      </span>
                    </div>
                    <span className={styles.transferDate}>{formatDate(transfer.requestedAt)}</span>
                  </div>
                  <div className={styles.transferBody}>
                    <div className={styles.staffInfo}>
                      <span className={styles.staffName}>{transfer.staffName}</span>
                      <span className={styles.staffFile}>{transfer.fileNumber} • {transfer.currentGrade}</span>
                    </div>
                    <div className={styles.transferRoute}>
                      <span className={styles.mdaName}>{transfer.sourceMDA.code}</span>
                      <span className={styles.routeArrow}>→</span>
                      <span className={styles.mdaName}>{transfer.destinationMDA.code}</span>
                    </div>
                  </div>
                  <div className={styles.transferFooter}>
                    <span className={styles.transferType}>{transferProtocol.getTypeLabel(transfer.type)}</span>
                    <span className={styles.transferReason}>{transferProtocol.getReasonLabel(transfer.reason)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Transfer Flow Diagram */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Transfer Workflow</h3>
            <div className={styles.workflowDiagram}>
              {[
                { step: 1, label: 'Source MDA', desc: 'Request & Approve' },
                { step: 2, label: 'Dispatch', desc: 'Send to Hub' },
                { step: 3, label: 'OHCS Hub', desc: 'Verify Records' },
                { step: 4, label: 'Transit', desc: 'Forward Records' },
                { step: 5, label: 'Destination MDA', desc: 'Receive & Confirm' },
              ].map((item, index) => (
                <div key={item.step} className={styles.workflowStep}>
                  <div className={styles.workflowIcon}>{item.step}</div>
                  <div className={styles.workflowContent}>
                    <span className={styles.workflowLabel}>{item.label}</span>
                    <span className={styles.workflowDesc}>{item.desc}</span>
                  </div>
                  {index < 4 && <div className={styles.workflowArrow}>→</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Transfer Tab */}
      {activeTab === 'new' && (
        <div className={styles.content}>
          <div className={styles.formContainer}>
            <h2 className={styles.formTitle}>Create Transfer Request</h2>
            <p className={styles.formDescription}>
              Initiate a transfer of staff records to another MDA through the OHCS Central Hub.
            </p>

            <div className={styles.formSection}>
              <h3>Transfer Type</h3>
              <div className={styles.typeGrid}>
                {([
                  { type: 'permanent_transfer', icon: '📁', desc: 'Full transfer to new MDA' },
                  { type: 'secondment', icon: '🔄', desc: 'Temporary posting' },
                  { type: 'temporary_assignment', icon: '⏱️', desc: 'Short-term assignment' },
                  { type: 'loan', icon: '🤝', desc: 'Inter-MDA loan' },
                ] as { type: TransferType; icon: string; desc: string }[]).map((item) => (
                  <div
                    key={item.type}
                    className={`${styles.typeCard} ${newTransfer.type === item.type ? styles.selected : ''}`}
                    onClick={() => setNewTransfer({ ...newTransfer, type: item.type })}
                  >
                    <span className={styles.typeIcon}>{item.icon}</span>
                    <span className={styles.typeName}>{transferProtocol.getTypeLabel(item.type)}</span>
                    <span className={styles.typeDesc}>{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.formSection}>
              <h3>Staff Information</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>File Number *</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={newTransfer.fileNumber}
                    onChange={(e) => setNewTransfer({ ...newTransfer, fileNumber: e.target.value })}
                    placeholder="e.g., OHCS-2015-0342"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Staff Name *</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={newTransfer.staffName}
                    onChange={(e) => setNewTransfer({ ...newTransfer, staffName: e.target.value })}
                    placeholder="Full name"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Current Grade</label>
                  <input
                    type="text"
                    className={styles.formInput}
                    value={newTransfer.currentGrade}
                    onChange={(e) => setNewTransfer({ ...newTransfer, currentGrade: e.target.value })}
                    placeholder="e.g., Grade 16"
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Priority</label>
                  <select
                    className={styles.formSelect}
                    value={newTransfer.priority}
                    onChange={(e) => setNewTransfer({ ...newTransfer, priority: e.target.value as 'urgent' | 'normal' | 'low' })}
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <h3>Transfer Details</h3>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Source MDA *</label>
                  <select
                    className={styles.formSelect}
                    value={newTransfer.sourceMDA}
                    onChange={(e) => setNewTransfer({ ...newTransfer, sourceMDA: e.target.value })}
                  >
                    <option value="">Select source MDA</option>
                    {mockMDAs.map((mda) => (
                      <option key={mda.code} value={mda.code}>{mda.name}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Destination MDA *</label>
                  <select
                    className={styles.formSelect}
                    value={newTransfer.destinationMDA}
                    onChange={(e) => setNewTransfer({ ...newTransfer, destinationMDA: e.target.value })}
                  >
                    <option value="">Select destination MDA</option>
                    {mockMDAs.filter(m => m.code !== newTransfer.sourceMDA).map((mda) => (
                      <option key={mda.code} value={mda.code}>{mda.name}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Reason</label>
                  <select
                    className={styles.formSelect}
                    value={newTransfer.reason}
                    onChange={(e) => setNewTransfer({ ...newTransfer, reason: e.target.value as TransferReason })}
                  >
                    <option value="posting_transfer">Posting Transfer</option>
                    <option value="promotion_transfer">Promotion Transfer</option>
                    <option value="secondment">Secondment</option>
                    <option value="request_transfer">Staff Request</option>
                    <option value="rationalization">Rationalization</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Effective Date *</label>
                  <input
                    type="date"
                    className={styles.formInput}
                    value={newTransfer.effectiveDate}
                    onChange={(e) => setNewTransfer({ ...newTransfer, effectiveDate: e.target.value })}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Notes</label>
                <textarea
                  className={styles.formTextarea}
                  value={newTransfer.notes}
                  onChange={(e) => setNewTransfer({ ...newTransfer, notes: e.target.value })}
                  placeholder="Additional information..."
                  rows={3}
                />
              </div>
            </div>

            <div className={styles.formActions}>
              <button className={styles.secondaryButton} onClick={() => setActiveTab('dashboard')}>
                Cancel
              </button>
              <button
                className={styles.primaryButton}
                onClick={handleCreateTransfer}
                disabled={!newTransfer.fileNumber || !newTransfer.staffName || !newTransfer.sourceMDA || !newTransfer.destinationMDA || !newTransfer.effectiveDate}
              >
                Create Transfer Request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Tab */}
      {activeTab === 'tracking' && (
        <div className={styles.content}>
          <div className={styles.trackingHeader}>
            <h2>Track Transfers</h2>
            <div className={styles.filterRow}>
              <select className={styles.filterSelect}>
                <option value="">All Statuses</option>
                <option value="pending_approval">Pending Approval</option>
                <option value="hub_processing">Hub Processing</option>
                <option value="in_transit">In Transit</option>
                <option value="completed">Completed</option>
              </select>
              <select className={styles.filterSelect}>
                <option value="">All Types</option>
                <option value="permanent_transfer">Permanent Transfer</option>
                <option value="secondment">Secondment</option>
                <option value="temporary_assignment">Temporary Assignment</option>
              </select>
            </div>
          </div>

          <div className={styles.trackingList}>
            {transfers.map((transfer) => (
              <div key={transfer.id} className={styles.trackingCard}>
                <div className={styles.trackingCardHeader}>
                  <div className={styles.trackingInfo}>
                    <span className={styles.trackingNumber}>{transfer.requestNumber}</span>
                    <span
                      className={styles.trackingStatus}
                      style={{ backgroundColor: `${getStatusColor(transfer.status)}20`, color: getStatusColor(transfer.status) }}
                    >
                      {transferProtocol.getStatusLabel(transfer.status)}
                    </span>
                  </div>
                  <button
                    className={styles.viewButton}
                    onClick={() => { setSelectedTransfer(transfer); setShowDetailModal(true); }}
                  >
                    View Details
                  </button>
                </div>

                <div className={styles.trackingBody}>
                  <div className={styles.trackingStaff}>
                    <span className={styles.trackingStaffName}>{transfer.staffName}</span>
                    <span className={styles.trackingStaffFile}>{transfer.fileNumber}</span>
                  </div>

                  <div className={styles.trackingRoute}>
                    <div className={styles.routePoint}>
                      <span className={styles.routeLabel}>From</span>
                      <span className={styles.routeValue}>{transfer.sourceMDA.name}</span>
                    </div>
                    <div className={styles.routeArrowBig}>→</div>
                    <div className={styles.routePoint}>
                      <span className={styles.routeLabel}>To</span>
                      <span className={styles.routeValue}>{transfer.destinationMDA.name}</span>
                    </div>
                  </div>
                </div>

                {/* Progress */}
                <div className={styles.trackingProgress}>
                  {['Source', 'Dispatch', 'OHCS Hub', 'Transit', 'Destination'].map((step, index) => {
                    const statusOrder = ['draft', 'pending_approval', 'approved', 'dispatched', 'hub_processing', 'hub_verified', 'in_transit', 'pending_receipt', 'received', 'completed'];
                    const stepMapping = [1, 3, 5, 7, 9];
                    const currentIndex = statusOrder.indexOf(transfer.status);
                    const isComplete = currentIndex >= stepMapping[index];
                    const isCurrent = currentIndex >= stepMapping[index] - 1 && currentIndex < stepMapping[index];

                    return (
                      <div key={step} className={`${styles.progressStep} ${isComplete ? styles.complete : ''} ${isCurrent ? styles.current : ''}`}>
                        <div className={styles.progressDot} />
                        <span className={styles.progressLabel}>{step}</span>
                      </div>
                    );
                  })}
                </div>

                <div className={styles.trackingMeta}>
                  <span>Requested: {formatDate(transfer.requestedAt)}</span>
                  <span>Effective: {formatDate(transfer.effectiveDate)}</span>
                  <span>{transferProtocol.getTypeLabel(transfer.type)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Verification Tab */}
      {activeTab === 'verification' && (
        <div className={styles.content}>
          <div className={styles.verificationHeader}>
            <h2>Verification Queue</h2>
            <p>Review and verify transfer requests at the OHCS Hub</p>
          </div>

          <div className={styles.verificationList}>
            {transfers.filter(t => t.status === 'hub_processing').map((transfer) => (
              <div key={transfer.id} className={styles.verificationCard}>
                <div className={styles.verificationCardHeader}>
                  <div>
                    <span className={styles.verificationNumber}>{transfer.requestNumber}</span>
                    <span className={styles.verificationStaff}>{transfer.staffName}</span>
                  </div>
                  <div className={styles.verificationProgress}>
                    <span>{getVerificationProgress(transfer)}% Complete</span>
                    <div className={styles.verificationBar}>
                      <div className={styles.verificationFill} style={{ width: `${getVerificationProgress(transfer)}%` }} />
                    </div>
                  </div>
                </div>

                <div className={styles.verificationChecklist}>
                  {transfer.verifications.map((v) => (
                    <div key={v.id} className={`${styles.checklistItem} ${styles[v.status]}`}>
                      <span className={styles.checklistIcon}>
                        {v.status === 'passed' ? '✓' : v.status === 'failed' ? '✗' : '○'}
                      </span>
                      <span className={styles.checklistLabel}>
                        {v.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                      <span className={styles.checklistStatus}>{v.status}</span>
                    </div>
                  ))}
                </div>

                <div className={styles.verificationActions}>
                  <button className={styles.verifyButton}>Continue Verification</button>
                  <button className={styles.rejectButton}>Reject Transfer</button>
                </div>
              </div>
            ))}

            {transfers.filter(t => t.status === 'hub_processing').length === 0 && (
              <div className={styles.emptyVerification}>
                <span className={styles.emptyIcon}>✓</span>
                <h3>No Pending Verifications</h3>
                <p>All transfers have been verified</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedTransfer && (
        <div className={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h2 className={styles.modalTitle}>{selectedTransfer.requestNumber}</h2>
                <span
                  className={styles.modalStatus}
                  style={{ backgroundColor: `${getStatusColor(selectedTransfer.status)}20`, color: getStatusColor(selectedTransfer.status) }}
                >
                  {transferProtocol.getStatusLabel(selectedTransfer.status)}
                </span>
              </div>
              <button className={styles.closeButton} onClick={() => setShowDetailModal(false)}>×</button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.modalSection}>
                <h4>Staff Information</h4>
                <div className={styles.modalGrid}>
                  <div>
                    <span className={styles.modalLabel}>Name</span>
                    <span className={styles.modalValue}>{selectedTransfer.staffName}</span>
                  </div>
                  <div>
                    <span className={styles.modalLabel}>File Number</span>
                    <span className={styles.modalValue}>{selectedTransfer.fileNumber}</span>
                  </div>
                  <div>
                    <span className={styles.modalLabel}>Grade</span>
                    <span className={styles.modalValue}>{selectedTransfer.currentGrade}</span>
                  </div>
                </div>
              </div>

              <div className={styles.modalSection}>
                <h4>Transfer Route</h4>
                <div className={styles.modalRoute}>
                  <div className={styles.modalMda}>
                    <span className={styles.modalMdaLabel}>Source</span>
                    <span className={styles.modalMdaName}>{selectedTransfer.sourceMDA.name}</span>
                    <span className={styles.modalMdaContact}>{selectedTransfer.sourceMDA.registryContact.name}</span>
                  </div>
                  <div className={styles.modalArrow}>→</div>
                  <div className={styles.modalMda}>
                    <span className={styles.modalMdaLabel}>Destination</span>
                    <span className={styles.modalMdaName}>{selectedTransfer.destinationMDA.name}</span>
                    <span className={styles.modalMdaContact}>{selectedTransfer.destinationMDA.registryContact.name}</span>
                  </div>
                </div>
              </div>

              <div className={styles.modalSection}>
                <h4>Timeline</h4>
                <div className={styles.timelineList}>
                  {selectedTransfer.timeline.slice().reverse().map((event) => (
                    <div key={event.id} className={styles.timelineItem}>
                      <div className={styles.timelineDot} />
                      <div className={styles.timelineContent}>
                        <span className={styles.timelineDesc}>{event.description}</span>
                        <span className={styles.timelineMeta}>
                          {event.actor} • {new Date(event.timestamp).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.secondaryButton} onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              {selectedTransfer.status === 'pending_approval' && (
                <button className={styles.primaryButton}>Approve Transfer</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
